import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FunctionComponent, ReactNode, SVGProps } from 'react';
import {
  SearchIcon,
  CaretIcon,
  CheckmarkIcon,
  HierarchyNodesIcon,
  SubInfoIcon,
} from '../../../assets/icons';
import { Spinner } from '../../../components/ui';
import { useAppDispatch } from '../../../app/hooks';
import { usePlatformState } from '../../../features/platform/platformStateSlice';
import { useGetStatesQuery as usePlatformStatesQuery } from '../../../features/platform/platformApi';
import { territoryApi } from '../../../features/territory/territoryApi';
import {
  EC_LEVEL,
  ROOT,
  canHaveChildren,
  fetchConstituencyChildren,
  statesToNodes,
  type Branch,
  type NodeKind,
  type StateSeed,
  type TreeNode,
} from '../../../features/territory/constituencyTree';

export type { NodeKind };

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;

/* ------------------------------------------------------------------ *
 * Dynamic tree model. The tree keeps the sample structure/visuals, but
 * every level below the (static) root is fetched from the territory API
 * when its parent is expanded — passing the parent's code down:
 *
 *   National Central Committee (static)
 *     └─ State            GET /territory/state/
 *        └─ Parliament    GET /territory/parliament/?state=<state.id>
 *           └─ Assembly   GET /territory/assembly/?pc=<pc.id>
 *              ├─ Urban   GET /territory/block-ulb/?type=ULB
 *              │            └─ Ward  ?block_ulb_code → Village ?ward_code&type=ULB → Booth ?village_result_code
 *              └─ Rural   GET /territory/gram-panchayat/?ac_code__startswith=<ac_code>
 *                           └─ Village ?gram_panchayat_lgd_code&type=PRI → Booth ?village_result_code
 * ------------------------------------------------------------------ */

/** Reverse of EC_LEVEL: level rank → name (for prefilled saved areas not yet loaded). */
const LEVEL_NAME_BY_RANK: Record<number, string> = Object.values(EC_LEVEL).reduce(
  (acc, v) => {
    if (v) acc[v.rank] = v.name;
    return acc;
  },
  {} as Record<number, string>,
);

/** The EC area key (rank:code) used to match a node against a saved boundary. */
const nodeAreaKey = (node: TreeNode): string | null => {
  const lvl = EC_LEVEL[node.kind];
  return lvl && node.areaCode ? `${lvl.rank}:${node.areaCode}` : null;
};

/** A selected Assembly Constituency reported to the wizard (for step 4's Level 1). */
export interface AcOption {
  id: string;
  label: string;
}

/** A checked EC area reported to the wizard for the bulk scope POST on Confirm. */
export interface SelectedArea {
  levelName: string;
  levelRank: number;
  areaCode: string;
}

/** A previously-saved boundary (from GET) used to prefill the tree in edit/view mode. */
export interface SavedArea {
  levelRank: number;
  areaCode: string;
}

/**
 * A pruned, read-only view of the hierarchy reported to the wizard for step 3.
 * Contains every selected node plus its ancestors (the path) and, under a
 * selected node, its full loaded subtree.
 */
export interface HierarchyView {
  id: string;
  label: string;
  icon?: IconType;
  level?: string;
  sub?: string;
  /** Node kind + code — lets step 4 fetch the booths under a selected village leaf. */
  kind?: NodeKind;
  code?: string;
  /** Real EC area code (ps_code for booths) — matched against the org tree in step 4. */
  areaCode?: string;
  selected: boolean;
  children: HierarchyView[];
}

/* ------------------------------------------------------------------ *
 * Shared bits
 * ------------------------------------------------------------------ */

function Checkbox({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={disabled ? undefined : onChange}
      className={[
        'flex size-[18px] shrink-0 items-center justify-center rounded transition-colors',
        disabled
          ? 'cursor-not-allowed border border-[#e2e8f0] bg-[#f1f5f9]'
          : checked
            ? 'bg-brand text-white'
            : 'border border-[#cbd5e1] bg-white',
      ].join(' ')}
    >
      {checked && !disabled && <CheckmarkIcon className="size-4" />}
    </button>
  );
}

function Caret({
  hasChildren,
  expanded,
  onClick,
}: {
  hasChildren: boolean;
  expanded?: boolean;
  onClick?: () => void;
}) {
  if (!hasChildren) return <span className="size-4 shrink-0" />;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex size-4 shrink-0 items-center justify-center text-[#94a3b8]"
      aria-label={expanded ? 'Collapse' : 'Expand'}
    >
      <CaretIcon className={['size-4 transition-transform', expanded ? '' : '-rotate-90'].join(' ')} />
    </button>
  );
}

function LevelBadge({ children }: { children: ReactNode }) {
  return (
    <span className="rounded bg-[#f1f5f9] px-2 py-1 text-[10px] font-bold tracking-[0.25px] text-[#64748b]">
      {children}
    </span>
  );
}

function SelectedPill() {
  return (
    <span className="flex items-center gap-1.5">
      <span className="size-2 rounded-full bg-[#10b981]" />
      <span className="text-[10px] font-bold tracking-[0.5px] text-[#059669]">SELECTED</span>
    </span>
  );
}

function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={[
        'flex h-[640px] flex-col overflow-hidden rounded-xl border border-line-soft bg-white shadow-topbar',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

const ROW = 'flex items-center gap-2.5 py-2';
const ROW_SELECTED =
  'flex items-center gap-2.5 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-3 py-2.5';

/* ------------------------------------------------------------------ *
 * Dynamic constituency tree
 * ------------------------------------------------------------------ */

interface ConstituencyTreeProps {
  /** Load the state tree only when this step is active. */
  active?: boolean;
  /** Filters the top-level states via the state API's ?search= param. */
  search?: string;
  /** Restrict the top-level states to this state name only (the org's selected state). */
  restrictStateName?: string;
  /** Top-level states (from /platform/states/) — used to build the root instead of lazy-loading. */
  rootStates?: StateSeed[];
  /** When true, checking a node clears any other — only one area can be selected. */
  singleSelect?: boolean;
  /** When true, insert a District level between Parliament and Assembly (Create User). */
  withDistrict?: boolean;
  onSelectionChange?: (nodes: HierarchyView[]) => void;
  onBranchesChange?: (branches: Record<string, Branch>) => void;
  onConstituenciesChange?: (acs: AcOption[]) => void;
  onAreasChange?: (areas: SelectedArea[]) => void;
  preselectedAreas?: SavedArea[];
}

/** Per-call rendering options for the shared tree engine. */
interface RenderOpts {
  /** Stop inline-expansion at this node kind (e.g. 'parliament' on the left panel); such a
      node instead calls `onFocus` to drive the right panel. */
  capKind?: NodeKind;
  focusedId?: string;
  onFocus?: (node: TreeNode) => void;
}

/**
 * Shared constituency-tree engine — owns the fetch/expand/select state and returns a
 * `renderNode(node, opts)` renderer so multiple panels (e.g. the split left/right boundary
 * view) can render different slices of the SAME selection state.
 */
function useConstituencyTree({
  active = true,
  search,
  restrictStateName,
  rootStates,
  singleSelect = false,
  withDistrict = false,
  onSelectionChange,
  onBranchesChange,
  onConstituenciesChange,
  onAreasChange,
  preselectedAreas,
}: ConstituencyTreeProps) {
  const dispatch = useAppDispatch();

  // Saved boundaries (edit/view) — auto-expanded + auto-checked in the tree.
  const savedAreas = useMemo(() => preselectedAreas ?? [], [preselectedAreas]);
  // Keyed by rank:code — used to prefill checks and to keep unloaded areas on save.
  const savedKeys = useMemo(
    () => new Set(savedAreas.map((a) => `${a.levelRank}:${a.areaCode}`)),
    [savedAreas],
  );

  const [expanded, setExpanded] = useState<Set<string>>(new Set(['root']));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [childrenMap, setChildrenMap] = useState<Record<string, TreeNode[]>>({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [errorIds, setErrorIds] = useState<Set<string>>(new Set());

  // Flat lookup + loaded-children cache, kept in refs (updated as nodes load).
  const nodeMap = useRef<Record<string, TreeNode>>({ root: ROOT });
  const childrenRef = useRef<Record<string, TreeNode[]>>({});

  // Edit mode: scroll the auto-selected (saved) node into view once it's revealed.
  const selectedRowRef = useRef<HTMLDivElement | null>(null);
  const didAutoScrollRef = useRef(false);
  useEffect(() => {
    if (didAutoScrollRef.current || savedAreas.length === 0 || selected.size === 0) return;
    const el = selectedRowRef.current;
    if (!el) return;
    didAutoScrollRef.current = true;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [selected, childrenMap, savedAreas]);

  /** Fetch a node's children; restrict the top-level states to the org's selected state. */
  const fetchChildren = useCallback(
    async (node: TreeNode) => {
      // Root: build the State list straight from GET /territory/state/ (passed as rootStates),
      // falling back to the lazy fetch if it hasn't resolved yet.
      const children =
        node.kind === 'root' && rootStates && rootStates.length
          ? statesToNodes(rootStates)
          : await fetchConstituencyChildren(dispatch, node, search, withDistrict);
      if (node.kind === 'root' && restrictStateName) {
        const target = restrictStateName.trim().toLowerCase();
        const scoped = children.filter((c) => (c.label || '').trim().toLowerCase() === target);
        // Scope to the selected state when it matches; otherwise fall back to the full list
        // (old design) so a name mismatch never leaves an empty tree with no drill-down.
        return scoped.length > 0 ? scoped : children;
      }
      return children;
    },
    [dispatch, search, withDistrict, restrictStateName, rootStates],
  );

  // Fetch + cache a node's children, and auto-check any that match a saved boundary.
  const fetchAndStore = useCallback(
    async (node: TreeNode): Promise<TreeNode[]> => {
      const kids = await fetchChildren(node);
      kids.forEach((k) => {
        nodeMap.current[k.id] = k;
      });
      childrenRef.current[node.id] = kids;
      setChildrenMap((m) => ({ ...m, [node.id]: kids }));
      if (savedKeys.size > 0) {
        const toCheck = kids
          .filter((k) => {
            const key = nodeAreaKey(k);
            return key !== null && savedKeys.has(key);
          })
          .map((k) => k.id);
        if (toCheck.length > 0) {
          setSelected((s) => {
            const next = new Set(s);
            toCheck.forEach((id) => next.add(id));
            return next;
          });
        }
      }
      return kids;
    },
    [fetchChildren, savedKeys],
  );

  const loadChildren = useCallback(
    async (node: TreeNode) => {
      setLoadingIds((s) => new Set(s).add(node.id));
      setErrorIds((s) => {
        const n = new Set(s);
        n.delete(node.id);
        return n;
      });
      try {
        await fetchAndStore(node);
      } catch {
        setErrorIds((s) => new Set(s).add(node.id));
      } finally {
        setLoadingIds((s) => {
          const n = new Set(s);
          n.delete(node.id);
          return n;
        });
      }
    },
    [fetchAndStore],
  );

  // Like loadChildren, but returns the children (for the auto-expand driver).
  const ensureChildren = useCallback(
    async (node: TreeNode): Promise<TreeNode[]> => {
      if (childrenRef.current[node.id]) return childrenRef.current[node.id];
      setLoadingIds((s) => new Set(s).add(node.id));
      try {
        return await fetchAndStore(node);
      } finally {
        setLoadingIds((s) => {
          const n = new Set(s);
          n.delete(node.id);
          return n;
        });
      }
    },
    [fetchAndStore],
  );

  // Resolve a saved area to a top-down list of node matchers (edit-mode reveal).
  const resolvePath = useCallback(
    async (area: SavedArea): Promise<((n: TreeNode) => boolean)[] | null> => {
      const ep = territoryApi.endpoints;
      const run = <T,>(p: { unwrap: () => Promise<T> }) => p.unwrap();
      const code = area.areaCode;

      const stateM = (id: number | string) => (n: TreeNode) =>
        n.kind === 'state' && n.code === String(id);
      const pcM = (id: number | string) => (n: TreeNode) =>
        n.kind === 'parliament' && n.code === String(id);
      const acM = (c: string) => (n: TreeNode) => n.kind === 'assembly' && n.code === c;
      const branchM = (b: Branch) => (n: TreeNode) => n.kind === 'branch' && n.branch === b;
      const gramM = (c: string) => (n: TreeNode) => n.kind === 'gram' && n.code === c;
      const villageM = (c: string) => (n: TreeNode) => n.kind === 'village' && n.code === c;

      // ac_code → { stateId, pcId } via assembly then parliament retrieve.
      const acToStatePc = async (acCode: string) => {
        try {
          const asm = await run(
            dispatch(ep.getAssemblyByAc.initiate({ acCode }, { subscribe: false })),
          );
          const pcId = asm?.[0]?.pc;
          if (pcId == null) return null;
          const pc = await run(
            dispatch(ep.getParliamentById.initiate({ id: pcId }, { subscribe: false })),
          );
          if (pc?.state == null) return null;
          return { stateId: pc.state, pcId };
        } catch {
          return null;
        }
      };

      try {
        if (area.levelRank === 1) {
          return [(n) => n.kind === 'state' && n.areaCode === code];
        }
        if (area.levelRank === 2) {
          const pcs = await run(
            dispatch(ep.getParliamentByPcCode.initiate({ pcCode: code }, { subscribe: false })),
          );
          const p = pcs?.[0];
          if (!p?.state) return null;
          return [stateM(p.state), pcM(p.id)];
        }
        if (area.levelRank === 4) {
          const c = await acToStatePc(code);
          if (!c) return null;
          return [stateM(c.stateId), pcM(c.pcId), acM(code)];
        }
        if (area.levelRank === 6) {
          const gp = await run(
            dispatch(ep.getGramByLgd.initiate({ lgd: code }, { subscribe: false })),
          );
          const acCode = gp?.[0]?.ac_code;
          if (!acCode) return null;
          const c = await acToStatePc(acCode);
          if (!c) return null;
          return [stateM(c.stateId), pcM(c.pcId), acM(acCode), branchM('rural'), gramM(code)];
        }
        if (area.levelRank === 7) {
          // Find the village's assembly via a booth (ps_code encodes ac_code).
          const psv = await run(
            dispatch(ep.getPsVillageByResult.initiate({ resultCode: code }, { subscribe: false })),
          );
          const psCode = psv?.[0]?.ps_code;
          if (!psCode) return null;
          const acCode = psCode.split('_').slice(0, 3).join('_');
          const c = await acToStatePc(acCode);
          if (!c) return null;
          const rural = await run(
            dispatch(ep.getVillagesByAc.initiate({ acCode, type: 'PRI' }, { subscribe: false })),
          );
          const v = rural.find((x) => String(x.village_result_code) === String(code));
          if (v?.gram_panchayat_lgd_code) {
            return [
              stateM(c.stateId),
              pcM(c.pcId),
              acM(acCode),
              branchM('rural'),
              gramM(v.gram_panchayat_lgd_code),
              villageM(code),
            ];
          }
          return null;
        }
        return null;
      } catch {
        return null;
      }
    },
    [dispatch],
  );

  // Edit-mode auto-expand: walk each saved area's path, expanding + checking it.
  const revealedRef = useRef<Set<string>>(new Set());
  const reveal = useCallback(
    async (area: SavedArea) => {
      // When the level rank is unknown (<= 0), try the resolvable ranks until one matches.
      // Order matters: deepest-first, because rank 1 (state) returns a matcher
      // unconditionally, so it must be tried last or it short-circuits deeper codes.
      const walkFrom = async (matchers: ((n: TreeNode) => boolean)[]) => {
        let node: TreeNode = ROOT;
        for (let i = 0; i < matchers.length; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          const kids = await ensureChildren(node);
          const next = kids.find(matchers[i]);
          if (!next) return false;
          const parentId = node.id;
          setExpanded((s) => (s.has(parentId) ? s : new Set(s).add(parentId)));
          if (i === matchers.length - 1) {
            const leafId = next.id;
            setSelected((s) => (s.has(leafId) ? s : new Set(s).add(leafId)));
          }
          node = next;
        }
        return true;
      };

      if (area.levelRank > 0) {
        const matchers = await resolvePath(area);
        if (matchers) await walkFrom(matchers);
        return;
      }
      for (const rank of [7, 6, 4, 2, 1]) {
        // eslint-disable-next-line no-await-in-loop
        const matchers = await resolvePath({ ...area, levelRank: rank });
        // eslint-disable-next-line no-await-in-loop
        if (matchers && (await walkFrom(matchers))) return;
      }
    },
    [resolvePath, ensureChildren],
  );

  useEffect(() => {
    if (savedAreas.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      for (const area of savedAreas) {
        const key = `${area.levelRank}:${area.areaCode}`;
        if (revealedRef.current.has(key)) continue;
        revealedRef.current.add(key);
        try {
          await reveal(area);
        } catch {
          /* ignore — the summary chip still shows it */
        }
        if (cancelled) return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [savedAreas, reveal]);

  // Load the states under the root once the step is active, and re-load them whenever
  // the search term changes (the state API filters via ?search=).
  useEffect(() => {
    if (!active) return;
    void loadChildren(ROOT);
    // Re-build the root when the state list arrives (0 → N) so it uses the fetched states.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, search, rootStates?.length]);

  const toggleExpand = (node: TreeNode) => {
    const willOpen = !expanded.has(node.id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (willOpen) next.add(node.id);
      else next.delete(node.id);
      return next;
    });
    if (willOpen && canHaveChildren(node) && !childrenMap[node.id] && !loadingIds.has(node.id)) {
      void loadChildren(node);
    }
  };

  const toggleSelect = (node: TreeNode) =>
    setSelected((prev) => {
      if (singleSelect) {
        // Only one area at a time — checking a node clears any other.
        return prev.has(node.id) ? new Set() : new Set([node.id]);
      }
      const next = new Set(prev);
      if (next.has(node.id)) next.delete(node.id);
      else next.add(node.id);
      return next;
    });

  // Report selection / branches / constituencies up to the wizard.
  useEffect(() => {
    const map = nodeMap.current;

    const build = (node: TreeNode, under = false): HierarchyView | null => {
      const isSel = selected.has(node.id);
      const inBranch = under || isSel;
      const kids = (childrenMap[node.id] || [])
        .map((c) => build(c, inBranch))
        .filter((x): x is HierarchyView => x !== null);
      if (inBranch || kids.length > 0) {
        return {
          id: node.id,
          label: node.label,
          icon: node.icon,
          level: node.level,
          sub: node.sub,
          kind: node.kind,
          code: node.code,
          areaCode: node.areaCode,
          selected: isSel,
          children: kids,
        };
      }
      return null;
    };

    const rootView = build(ROOT);
    onSelectionChange?.(rootView ? [rootView] : []);

    if (onBranchesChange) {
      const byAc: Record<string, Branch> = {};
      selected.forEach((id) => {
        const n = map[id];
        if (n?.branch && n.acId) byAc[n.acId] = n.branch;
      });
      onBranchesChange(byAc);
    }

    if (onConstituenciesChange) {
      const acs: AcOption[] = [];
      const seen = new Set<string>();
      selected.forEach((id) => {
        const n = map[id];
        if (n?.acId && !seen.has(n.acId)) {
          seen.add(n.acId);
          acs.push({ id: n.acId, label: n.acLabel || n.acId });
        }
      });
      onConstituenciesChange(acs);
    }

    if (onAreasChange) {
      const areas: SelectedArea[] = [];
      const emitted = new Set<string>();
      // Every checked node that maps to an EC level + has a real area code.
      selected.forEach((id) => {
        let n = map[id];
        // A Rural/Urban branch isn't an EC area itself — record its parent assembly instead.
        if (n?.kind === 'branch' && n.acId) n = map[n.acId] ?? n;
        const lvl = n && EC_LEVEL[n.kind];
        if (!n || !lvl || !n.areaCode) return;
        const key = `${lvl.rank}:${n.areaCode}`;
        if (emitted.has(key)) return;
        areas.push({ levelName: lvl.name, levelRank: lvl.rank, areaCode: n.areaCode });
        emitted.add(key);
      });
      // Keep saved boundaries whose node hasn't loaded yet (so nothing is lost on
      // re-confirm). Once a saved node loads it's auto-checked → emitted above; if the
      // user then unchecks it, its node exists but isn't selected → correctly dropped.
      const loadedKeys = new Set<string>();
      Object.values(map).forEach((n) => {
        const key = nodeAreaKey(n);
        if (key) loadedKeys.add(key);
      });
      savedAreas.forEach((a) => {
        // Skip unknown-rank placeholders (rank <= 0) — only the resolved node is emitted.
        if (a.levelRank <= 0) return;
        const key = `${a.levelRank}:${a.areaCode}`;
        if (!loadedKeys.has(key) && !emitted.has(key)) {
          areas.push({
            levelName: LEVEL_NAME_BY_RANK[a.levelRank] ?? '',
            levelRank: a.levelRank,
            areaCode: a.areaCode,
          });
          emitted.add(key);
        }
      });
      onAreasChange(areas);
    }
  }, [
    selected,
    childrenMap,
    savedAreas,
    onSelectionChange,
    onBranchesChange,
    onConstituenciesChange,
    onAreasChange,
  ]);

  // Which settlement branch (urban/rural) is chosen per AC — the other is disabled.
  const chosenBranch: Record<string, Branch> = {};
  selected.forEach((id) => {
    const n = nodeMap.current[id];
    if (n?.branch && n.acId) chosenBranch[n.acId] = n.branch;
  });
  const isDisabled = (node: TreeNode) =>
    Boolean(
      node.branch && node.acId && chosenBranch[node.acId] && chosenBranch[node.acId] !== node.branch,
    );

  const renderNode = (node: TreeNode, opts: RenderOpts = {}): ReactNode => {
    const { capKind, focusedId, onFocus } = opts;
    // A capped node (e.g. parliament on the left) doesn't expand inline; it focuses the
    // right panel instead.
    const atCap = !!capKind && node.kind === capKind;
    const expandable = canHaveChildren(node) && !atCap;
    const open = expanded.has(node.id);
    const isSel = selected.has(node.id);
    const isFocused = focusedId != null && node.id === focusedId;
    const disabled = isDisabled(node);
    const kids = childrenMap[node.id];
    const isLoading = loadingIds.has(node.id);
    const hasError = errorIds.has(node.id);

    return (
      <div key={node.id}>
        <div
          ref={isSel ? selectedRowRef : undefined}
          className={[isSel || isFocused ? ROW_SELECTED : ROW, disabled ? 'opacity-60' : ''].join(' ')}
        >
          {atCap ? (
            <button
              type="button"
              onClick={() => onFocus?.(node)}
              aria-label="Show EC levels"
              className="flex size-4 shrink-0 items-center justify-center text-[#94a3b8]"
            >
              <CaretIcon className="size-4 -rotate-90" />
            </button>
          ) : (
            <Caret hasChildren={expandable} expanded={open} onClick={() => toggleExpand(node)} />
          )}
          <Checkbox checked={isSel} disabled={disabled} onChange={() => toggleSelect(node)} />
          {node.icon ? (
            <node.icon className="size-5 shrink-0 text-brand" />
          ) : (
            <span className="flex size-5 shrink-0 items-center justify-center">
              <span className="size-1.5 rounded-full bg-[#94a3b8]" />
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              if (atCap) onFocus?.(node);
              else if (expandable) toggleExpand(node);
              else if (!disabled) toggleSelect(node);
            }}
            className={[
              'text-left text-sm',
              disabled
                ? 'text-[#94a3b8]'
                : isSel || isFocused
                  ? 'font-semibold text-brand'
                  : 'font-medium text-ink',
            ].join(' ')}
          >
            {node.label}
          </button>
          {node.level ? (
            <span className="ml-auto">
              <LevelBadge>{node.level}</LevelBadge>
            </span>
          ) : (
            isSel && (
              <span className="ml-auto">
                <SelectedPill />
              </span>
            )
          )}
        </div>

        {node.sub && (
          <div className="flex items-center gap-1.5 pl-8 pt-1 text-xs text-[#94a3b8]">
            <SubInfoIcon className="h-[9px] w-[10px]" />
            {node.sub}
          </div>
        )}

        {expandable && open && (
          <div className="ml-[19px] flex flex-col gap-1.5 border-l border-dashed border-[#cbd5e1] pl-3">
            {node.groupLabel && (
              <p className="py-1 pl-6 text-xs font-medium text-[#94a3b8]">{node.groupLabel}</p>
            )}
            {isLoading && (
              <div className="flex items-center gap-2 py-2 pl-6 text-xs text-[#94a3b8]">
                <Spinner size={14} /> Loading…
              </div>
            )}
            {hasError && (
              <div className="flex items-center gap-2 py-2 pl-6 text-xs text-red-500">
                Failed to load.
                <button
                  type="button"
                  onClick={() => void loadChildren(node)}
                  className="font-semibold underline"
                >
                  Retry
                </button>
              </div>
            )}
            {!isLoading && !hasError && kids && kids.length === 0 && (
              <p className="py-2 pl-6 text-xs text-[#94a3b8]">No items found.</p>
            )}
            {!isLoading && !hasError && kids?.map((k) => renderNode(k, opts))}
          </div>
        )}
      </div>
    );
  };

  return { renderNode, childrenMap, loadChildren, nodeMap, loadingIds, selected };
}

/** Backwards-compatible single-tree renderer (used where the whole tree is shown in one panel). */
export function ConstituencyTree(props: ConstituencyTreeProps) {
  const tree = useConstituencyTree(props);
  return <div className="flex flex-col gap-1">{tree.renderNode(ROOT)}</div>;
}

/* ------------------------------------------------------------------ *
 * Step
 * ------------------------------------------------------------------ */

/**
 * Step 2 — Define Operational Boundary. Split into two linked panels sharing one selection:
 *  - Left  "Select Constituency Node": State → Parliament (capped). Focusing a Parliament…
 *  - Right "Election Commission Level Hierarchy": …loads its Assembly → Rural/Urban → … → Booth.
 * Checks in either panel are aggregated for the wizard's bulk EC-scope save.
 */
export default function DefineBoundaryStep({
  active,
  onSelectionChange,
  onBranchesChange,
  onConstituenciesChange,
  onAreasChange,
  preselectedAreas,
}: {
  /** Load the state tree only when this step is active. */
  active?: boolean;
  onSelectionChange?: (nodes: HierarchyView[]) => void;
  onBranchesChange?: (branches: Record<string, Branch>) => void;
  onConstituenciesChange?: (acs: AcOption[]) => void;
  onAreasChange?: (areas: SelectedArea[]) => void;
  preselectedAreas?: SavedArea[];
}) {
  // Search box filters the top-level states via the state API's ?search= param (debounced).
  const [query, setQuery] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setStateSearch(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  // The Parliament whose EC levels are shown on the right.
  const [focused, setFocused] = useState<string | null>(null);

  // Scope the tree to the active platform state (the state selected for this org).
  const { name: stateName } = usePlatformState();

  // Load the onboarded states from GET /platform/states/ whenever this step is mounted. Its rows
  // build the tree root directly; the parliament drill-down keys on each state's ECI state_id.
  const { data: statesData = [] } = usePlatformStatesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const tree = useConstituencyTree({
    active,
    search: stateSearch,
    restrictStateName: stateName,
    rootStates: statesData,
    onSelectionChange,
    onBranchesChange,
    onConstituenciesChange,
    onAreasChange,
    preselectedAreas,
  });

  const onFocus = (node: TreeNode) => {
    setFocused(node.id);
    if (!tree.childrenMap[node.id]) void tree.loadChildren(node);
  };

  // Edit mode: once a saved selection is revealed, auto-focus its Parliament so the right panel
  // loads its EC levels without a manual click. Runs once (until the user changes focus).
  const autoFocusedRef = useRef(false);
  useEffect(() => {
    if (autoFocusedRef.current || focused || tree.selected.size === 0) return;
    // Parent lookup from the loaded children, to walk a selected node up to its Parliament.
    const parentOf: Record<string, string> = {};
    Object.entries(tree.childrenMap).forEach(([pid, kids]) => {
      kids.forEach((k) => {
        parentOf[k.id] = pid;
      });
    });
    const parliamentOf = (id: string): TreeNode | null => {
      let cur: string | undefined = id;
      while (cur) {
        const n = tree.nodeMap.current[cur];
        if (n?.kind === 'parliament') return n;
        cur = parentOf[cur];
      }
      return null;
    };
    for (const id of Array.from(tree.selected)) {
      const p = parliamentOf(id);
      if (p) {
        autoFocusedRef.current = true;
        setFocused(p.id);
        if (!tree.childrenMap[p.id]) void tree.loadChildren(p);
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree.selected, tree.childrenMap, focused]);

  const focusedNode = focused ? tree.nodeMap.current[focused] : null;
  const assemblies = focused ? tree.childrenMap[focused] : undefined;
  const rightLoading = focused ? tree.loadingIds.has(focused) : false;

  return (
    <div className="flex flex-col gap-6">
      {/* Search */}
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-[#94a3b8]" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search location"
          className="w-full rounded-lg border border-line-soft bg-white py-3.5 pl-[41px] pr-3.5 text-sm text-ink shadow-card placeholder:text-[#94a3b8] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
        />
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Left — Select Constituency Node (State → Parliament) */}
        <Card className="min-w-0 flex-1">
          <div className="flex items-center justify-between border-b border-[#f1f5f9] px-5 py-4">
            <div className="flex items-center gap-3">
              <span className="flex size-8 items-center justify-center rounded-md bg-[#eff6ff] text-brand">
                <HierarchyNodesIcon className="size-5" />
              </span>
              <h2 className="text-base font-semibold text-ink">Select Constituency Node</h2>
            </div>
          </div>

          <div className="flex-1 overflow-auto px-4 py-6">
            <div className="flex flex-col gap-1">
              {tree.renderNode(ROOT, {
                capKind: 'parliament',
                focusedId: focused ?? undefined,
                onFocus,
              })}
            </div>
          </div>

          <div className="border-t border-[#f1f5f9] px-6 py-4">
            <button
              type="button"
              disabled={!focusedNode}
              onClick={() => focusedNode && onFocus(focusedNode)}
              className="w-full rounded-lg bg-brand py-3 text-center text-sm font-medium text-white shadow-card transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              Confirm Node
            </button>
          </div>
        </Card>

        {/* Right — Election Commission Level Hierarchy (Assembly → … → Booth) */}
        <Card className="w-full lg:w-[384px]">
          <div className="flex flex-col gap-2 px-6 pb-4 pt-6">
            <p className="text-xs font-bold uppercase tracking-[0.6px] text-brand">
              Governance Framework
            </p>
            <h3 className="text-base font-semibold text-ink">Election Commission Level Hierarchy</h3>
          </div>

          <div className="flex-1 overflow-auto px-6 pb-6">
            {!focusedNode ? (
              <p className="pt-4 text-sm text-ink-secondary">
                Select a Parliament constituency on the left to view its Election Commission levels.
              </p>
            ) : rightLoading && !assemblies ? (
              <div className="flex items-center gap-2 pt-4 text-sm text-ink-secondary">
                <Spinner size={16} /> Loading…
              </div>
            ) : assemblies && assemblies.length > 0 ? (
              <div className="flex flex-col gap-1">{assemblies.map((a) => tree.renderNode(a))}</div>
            ) : (
              <p className="pt-4 text-sm text-ink-secondary">
                No Election Commission levels found for {focusedNode.label}.
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
